import React from 'react';
// Ressources
import CSVUtils from './csvUtils';
import DateUtils from './dateUtils';
import FormattingUtils from './formattingUtils';
import HtmlUtils from './htmlUtils';
import ObjectUtils from './objectUtils';
import { FormatType, HeadCell } from '../local-types';

// Excel on a French locale expects ';' and needs a BOM to read UTF-8 accents
const CSV_DELIMITER = ';';
const CSV_BOM = '\uFEFF';
const CSV_MIME_TYPE = 'text/csv;charset=utf-8';

// Tags rendered on their own line: their content must not stick to the next one
const BLOCK_TAGS = ['br', 'div', 'p', 'li', 'tr', 'section', 'table'];
// Guard against a formatCell returning a self referencing tree
const MAX_NODE_DEPTH = 15;

// Filter keys sent to the API, ie search[name], filters[status], after[created_at], in[status]
const FILTER_KEY_REGEX = /^(search|filters|before|after|in)\[(.+?)\](?:\[(.+?)\])?$/;
const ACTION_COLUMN_IDS = ['action', 'actions'];
// Keep the filters part of a filename readable
const MAX_FILTERS_SLUG_LENGTH = 80;

/**
 * Extract the plain text a React node displays, so a cell rendered as JSX
 * (links, chips, fragments...) can be exported as the string the user sees.
 */
function reactNodeToText(node: any, depth: number = 0): string {
    if (node === null || node === undefined || typeof node === 'boolean') {
        return '';
    }
    if (typeof node === 'string') {
        return node;
    }
    if (typeof node === 'number') {
        return String(node);
    }
    if (depth > MAX_NODE_DEPTH) {
        return '';
    }
    if (Array.isArray(node)) {
        return node.map((child) => reactNodeToText(child, depth + 1)).join('');
    }
    if (React.isValidElement(node)) {
        const props = node.props as any;
        if (node.type === 'br') {
            return ' ';
        }
        // Fallbacks for components displaying their text through a prop (Chip, TooltipText...)
        let text = reactNodeToText(props?.children, depth + 1);
        if (!text.trim()) {
            text = reactNodeToText(props?.label ?? props?.title ?? props?.href, depth + 1);
        }
        if (typeof node.type === 'string' && BLOCK_TAGS.includes(node.type)) {
            return `${text} `;
        }
        return text;
    }
    return '';
}

const cleanText = (text: string): string => text.replace(/\s+/g, ' ').trim();

/**
 * Value of a single cell as displayed by the table, mirroring table-row rendering
 */
function getExportCellValue(row: any, header: HeadCell, t: any): string {
    const value = ObjectUtils.getSubValueFromKey(row, header.id);
    const formattedValue = FormattingUtils.format(value, header.type, t);

    try {
        // An editable picker renders a <Select/>: export the label of the selected option
        if (header.action && header.type === FormatType.Picker) {
            if (header.formatCell) {
                return cleanText(reactNodeToText(header.formatCell(row)));
            }
            const option = header.pickerOptions?.find((o) => String(o.value) === String(value));
            return cleanText(reactNodeToText(option ? option.label : value));
        }

        // Editable chips render one chip per option: export the activated ones
        if (header.action && header.type === FormatType.Chips) {
            return (header.chipsOptions ?? [])
                .filter((option) => header.chipsTest?.(row, formattedValue, option))
                .map((option) => cleanText(reactNodeToText(option.name)))
                .filter(Boolean)
                .join(', ');
        }

        // A single on/off chip
        if (header.type === FormatType.BooleanChips) {
            const activated = header.chipsTest?.(row, formattedValue);
            return cleanText(reactNodeToText(header.chipsOptions?.[activated ? 0 : 1]));
        }

        // An editable boolean renders a <Checkbox/>
        if (header.action && header.type === FormatType.Boolean) {
            return value ? t('common:true') : t('common:false');
        }

        if (header.loaderCell) {
            const loaded = cleanText(reactNodeToText(header.loaderCell(row, header.id)));
            if (loaded) {
                return loaded;
            }
        }

        if (header.formatCell) {
            return cleanText(reactNodeToText(header.formatCell(row)));
        }

        if (Array.isArray(formattedValue)) {
            return formattedValue
                .map((item) => cleanText(reactNodeToText(item)))
                .filter(Boolean)
                .join(', ');
        }

        return cleanText(reactNodeToText(formattedValue));
    } catch (error) {
        // A cell only made of actions (buttons, dialogs...) has nothing to export
        console.warn('Unable to export cell', header.id, error);
        return '';
    }
}

interface ActiveFilter {
    prefix: string; // search, filters, before, after or in
    field: string;
    values: string[]; // "in" filters hold several values
}

/**
 * Active filters of a table, ignoring the ones left empty
 */
function getActiveFilters(filters: any): ActiveFilter[] {
    return Object.keys(filters ?? {})
        .map((key) => {
            const raw = filters[key];
            const values = (Array.isArray(raw) ? raw : [raw])
                .filter((item) => item !== null && item !== undefined && item !== '' && typeof item !== 'object')
                .map((item) => String(item));
            if (values.length === 0) {
                return null;
            }
            const match = FILTER_KEY_REGEX.exec(key);
            return {
                prefix: match?.[1] ?? '',
                field: match?.[2] ?? key,
                values,
            };
        })
        .filter(Boolean);
}

/**
 * Name of a filtered field, preferring the label of its column so it reads
 * like the table the user is looking at
 */
function getFilterFieldLabel(filter: ActiveFilter, headers: HeadCell[], t: any): string {
    const header = headers.find((h) => h.idSearch === filter.field || h.id === filter.field);
    return header?.label || t(`common:table.${filter.field}`, { defaultValue: filter.field });
}

/**
 * Human readable filter, using the same wording as the filter chips of the table
 */
function getFilterLabel(filter: ActiveFilter, headers: HeadCell[], t: any): string {
    const field = getFilterFieldLabel(filter, headers, t);
    const name = filter.prefix ? t(filter.prefix, { cle: field }) : field;
    return `${name} : ${filter.values.map((value) => t(value)).join(', ')}`;
}

const slugify = (value: string): string =>
    value
        ?.toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '') ?? '';

/**
 * Filters as a compact filename fragment, capped so the name stays usable
 */
function getFiltersSlug(filters: ActiveFilter[]): string {
    const slug = filters
        .map((filter) => {
            const values = filter.values.join('_');
            if (filter.prefix === 'after') {
                return slugify(`${filter.field}_du_${values}`);
            }
            if (filter.prefix === 'before') {
                return slugify(`${filter.field}_au_${values}`);
            }
            return slugify(`${filter.field}_${values}`);
        })
        .filter(Boolean)
        .join('__');

    if (slug.length <= MAX_FILTERS_SLUG_LENGTH) {
        return slug;
    }
    // Cut on a separator so the last kept filter stays readable
    const cut = slug.slice(0, MAX_FILTERS_SLUG_LENGTH);
    return `${cut.slice(0, cut.lastIndexOf('__')) || cut}__suite`;
}

/**
 * Build the filename of an export, without its extension
 */
function getExportFilename(base: string, filters: ActiveFilter[]): string {
    const parts = [
        slugify(base) || 'export',
        getFiltersSlug(filters),
        DateUtils.dateFormat(new Date(), 'yyyy-mm-dd_HH-MM'),
    ];
    return parts.filter(Boolean).join('_');
}

/**
 * A column only made of buttons (edit, delete, credit note...) has nothing to export.
 * Some of them don't use the 'action' id (bills use 'credit'), hence the label check
 */
function isActionColumn(header: HeadCell, t: any): boolean {
    return ACTION_COLUMN_IDS.includes(header.id) || header.label === t('common:action');
}

/**
 * Export the rows currently displayed by a table, as an Excel readable file
 */
export function exportTableDatas(
    datas: any[],
    allHeaders: HeadCell[],
    t: any,
    filenameBase: string,
    filters?: any,
): void {
    const headers = allHeaders.filter((header) => !isActionColumn(header, t));
    const activeFilters = getActiveFilters(filters);
    const filtersSummary = activeFilters.length
        ? activeFilters.map((filter) => getFilterLabel(filter, headers, t)).join(' | ')
        : t('exportNoFilter');

    const contents = [
        [`${t('exportFilters')} : ${filtersSummary}`],
        headers.map((header) => header.label),
        ...datas.map((row) => headers.map((header) => getExportCellValue(row, header, t))),
    ];

    const csv = `${CSV_BOM}${CSVUtils.getCSVfromData(contents, CSV_DELIMITER)}`;
    HtmlUtils.createDownloadAction(csv, CSV_MIME_TYPE, getExportFilename(filenameBase, activeFilters));
}
